import React from 'react';
import PropTypes from 'prop-types';
import autobind from 'class-autobind';
import classNames from 'classnames';


// Context
import { OverviewConsumer, OverviewContext } from 'pages/Overview/OverviewContext';


// Config
import {
	defaults as Defaults,
	formatting,
	language as Lang,
	toolbarOptions as ToolbarOptions
} from 'pages/Overview/config';


// Lodash
import { capitalize, get, isNumber } from 'lodash';


// CSS
import styles from './styles.css';


// Local config
const CONFIG = {
	source: 'toolbar'
};


/**
 * Images
 */
/* eslint-disable global-require */
const Icons = {
	AlignCenter: require('../images/optimized/aligncenter.svg'),
	AlignLeft: require('../images/optimized/alignleft.svg'),
	AlignRight: require('../images/optimized/alignright.svg'),
	Leading: require('../images/optimized/leading.svg'),
	Margin: require('../images/optimized/margin.svg'),
	Size: require('../images/optimized/size.svg'),
	Tracking: require('../images/optimized/tracking.svg')
};
/* eslint-enable global-require */


/**
 * Settings panel, containing size, tracking, leading, and margin.
 *
 * @method Settings
 * @param  {Object} values  Collection of slider values pulled from state.
 * @param  {Func}   update  Method that updates the store’s settings object based on which setting type is passing value.
 * @return {Node}
 */
class Settings extends React.PureComponent {
	constructor(props) {
		super(props);
		autobind(this);

		this.state = {
			editorCaretPosition: null,
			isInitiated: false,
			size: {
				source: CONFIG.source,
				value: Defaults.size
			},
			leading: {
				source: CONFIG.source,
				value: Defaults.leading
			},
			pm: {
				source: CONFIG.source,
				value: Defaults.pm
			},
			tracking: {
				source: CONFIG.source,
				value: Defaults.tracking
			}
		};
	}


	componentDidMount() {
		const { leading, pm, size, tracking } = this.context.settings;

		this.setState({
			...this.state,
			leading: { ...this.state.leading, value: leading.value },
			pm: { ...this.state.pm, value: pm.value },
			size: { ...this.state.size, value: size.value },
			tracking: { ...this.state.tracking, value: tracking.value }
		});
	}


	componentDidUpdate(prevProps, prevState) {
		const { leading, pm, size, tracking } = this.context.settings;

		// A ridiculously fragile way to get the initial values for the sliders.
		// The sliders are all populated when they load with values taken from
		// `defaults` in the Overview config file. Those values need to be
		// updated once the visitor click into the Editor.
		if (
			[size.source, leading.source, pm.source, tracking.source].indexOf('editor') >= 0
			&& !this.state.isInitiated
		) {
			this.setState({ isInitiated: true });
			setTimeout(() => {
				this.setState({
					isInitiated: true,
					leading: { ...this.state.leading, value: this.context.settings.leading.value },
					pm: { ...this.state.pm, value: this.context.settings.pm.value },
					size: { ...this.state.size, value: this.context.settings.size.value },
					tracking: { ...this.state.tracking, value: this.context.settings.tracking.value }
				});
			}, 100);
		}

		if (this.context.caretPosition !== this.state.editorCaretPosition) {
			this.setState({ editorCaretPosition: this.context.caretPosition });
		}


		// This group of updates comes from the Editor. It’s triggered when the
		// caret moves in the Editor, and updates local state with any updates values.
		if (prevState.editorCaretPosition !== this.state.editorCaretPosition) {
			if (this.context.settings.size.value !== this.state.size.value) {
				this.setState({ size: { ...this.state.size, value: size.value } });
			}

			if (this.context.settings.leading.value !== this.state.leading.value) {
				this.setState({ leading: { ...this.state.leading, value: leading.value } });
			}

			if (this.context.settings.pm.value !== this.state.pm.value) {
				this.setState({ pm: { ...this.state.pm, value: pm.value } });
			}

			if (this.context.settings.tracking.value !== this.state.tracking.value) {
				this.setState({ tracking: { ...this.state.tracking, value: tracking.value } });
			}
		}
	}


	/**
	 * Set decimal point to two places and toggle class based on
	 * negative/positive value. End result should look something like -1.05
	 *
	 * @method labelFixedSize
	 * @param  {Number}     value numeric value to format
	 * @return {Node}       formatted label node
	 */
	labelFixedSize(value) {
		const decimal =
			isNaN(value)
				? 0
				: Number(value.toFixed(formatting.decimalPoints).toString());

		return decimal;
	}


	/**
	 * Generate input range for the setting type that’s passed in.
	 *
	 * @method renderRange
	 * @param  {String}    type size|tracking|leading|pm
	 * @return {Node}
	 */
	renderRange(type) {
		const { putSetting, selectionIsCollapsed } = this.context;

		const value = get(this.state[type], 'value');
		const typeToCapitalize = capitalize(type);
		const typeToUppercase = type.toUpperCase();

		return (
			<input
				{ ...ToolbarOptions[type] }
				className={ classNames(styles.rangeInput, { [styles['panelRow--isDisabled']]: selectionIsCollapsed }) }
				onChange={ event => {
					this.setState({ [type]: { ...this.state[type], value: Number(event.target.value), source: CONFIG.source } });
				} }
				onKeyDown={ event => event.preventDefault() }
				onMouseDown={ event => {
					this.setState({ [`prev${typeToCapitalize}Value`]: Number(event.target.value) });
				} }
				onMouseUp={ event => {
					if (!selectionIsCollapsed || selectionIsCollapsed && type === 'pm') {
						putSetting({ type: typeToUppercase, value: Number(event.target.value), silent: false, source: CONFIG.source });
					} else {
						this.setState({ [type]: { ...this.state[type], value: this.state[`prev${typeToCapitalize}Value`] } });
					}
				} }
				type="range"
				value={ isNumber(value) ? value : null }
			/>
		);
	}


	render() {
		const { settingsPanelRef, isOpen } = this.props;
		const { leading: theLeading, pm: thePM, size: theSize, tracking: theTracking } = this.state;

		const leading = get(theLeading, 'value');
		const pm = get(thePM, 'value');
		const size = get(theSize, 'value');
		const tracking = get(theTracking, 'value');

		return (
			<OverviewConsumer>
				{ ({ editorTextIsSelected, putSetting, settings }) =>
					<div className={ classNames(styles.panel, { [styles['panel--active']]: isOpen }) } ref={ settingsPanelRef }>
						<div className={ styles.panelInterior }>
							<div className={ styles.sliderGroup }>
								<div className={ classNames(styles.panelRow, { [styles['panelRow---isDisabled']]: !editorTextIsSelected }) }>
									<Icons.Size className={ styles.settingsIcon } />
									<div className={ styles.label }>
										{ size === Lang.multipleStylesSelected ? '—' : this.labelFixedSize(size) }
									</div>
									{ this.renderRange('size') }
								</div>

								<div className={ classNames(styles.panelRow, { [styles['panelRow---isDisabled']]: !editorTextIsSelected }) }>
									<Icons.Tracking className={ styles.settingsIcon } />
									<div className={ classNames(styles.label, { [styles['label--negative']]: get(settings, 'tracking.value', 0) < 0 }) }>
										{ tracking === Lang.multipleStylesSelected ? '—' : this.labelFixedSize(tracking) }
									</div>
									{ this.renderRange('tracking') }
								</div>

								<div className={ styles.panelRow }>
									<Icons.Leading className={ styles.settingsIcon } />
									<div className={ styles.label }>
										{ leading === Lang.multipleStylesSelected ? '—' : this.labelFixedSize(leading) }
									</div>
									{ this.renderRange('leading') }
								</div>

								<div className={ styles.panelRow }>
									<Icons.Margin className={ styles.settingsIcon } />
									<div className={ styles.label }>
										{ pm === Lang.multipleStylesSelected ? '—' : this.labelFixedSize(pm) }
									</div>
									{ this.renderRange('pm') }
								</div>
							</div>
							<div className={ classNames(styles['panelRow--alignment'], styles.alignmentGroup) }>
								<button
									className={ classNames(styles['alignmentButton--left'], { [styles['alignmentButton--active']]: get(settings, 'alignment.value', 'left') === 'left' }) }
									onMouseDown={ () => putSetting({ type: 'ALIGNMENT', value: 'left', silent: false, source: CONFIG.source }) }
								>
									<Icons.AlignLeft />
								</button>
								<button
									className={ classNames(styles['alignmentButton--center'], { [styles['alignmentButton--active']]: get(settings, 'alignment.value', null) === 'center' }) }
									onMouseDown={ () => putSetting({ type: 'ALIGNMENT', value: 'center', silent: false, source: CONFIG.source }) }
								>
									<Icons.AlignCenter />
								</button>
								<button
									className={ classNames(styles['alignmentButton--right'], { [styles['alignmentButton--active']]: get(settings, 'alignment.value', null) === 'right' }) }
									onMouseDown={ () => putSetting({ type: 'ALIGNMENT', value: 'right', silent: false, source: CONFIG.source }) }
								>
									<Icons.AlignRight />
								</button>
							</div>
						</div>
					</div>
				}
			</OverviewConsumer>
		);
	}
}


Settings.contextType = OverviewContext;


Settings.propTypes = {
	settingsPanelRef: PropTypes.func,
	isOpen: PropTypes.bool
};


Settings.defaultProps = {
	settingsPanelRef: null,
	isOpen: false
};


export default Settings;
